<template>
  <article class="free-pick-product">
    <div class="free-pick-product__cover">
      <img v-if="image" :src="image" :alt="product.name" loading="lazy">
      <span v-else class="free-pick-product__placeholder">{{ product.name }}</span>
      <span v-if="badge" class="free-pick-product__badge">{{ badge }}</span>
      <ProductPlay
        v-if="hasPreview"
        class="free-pick-product__play"
        type="button"
        :aria-label="`Play or pause audio preview for ${product.name}`"
        :product-details="playerDetails"
        medium-size />
    </div>
    <h4 class="free-pick-product__name"><NuxtLink :to="product.uri">{{ product.name }}</NuxtLink></h4>
    <p v-if="product.productVendor?.name" class="free-pick-product__vendor">{{ product.productVendor.name }}</p>
    <p class="free-pick-product__price">
      <s v-if="product.freePickNormalPrice" v-html="product.freePickNormalPrice" />
      <strong>Free</strong>
    </p>
    <slot />
  </article>
</template>

<script setup lang="ts">
import { computed } from '#imports';
import ProductPlay from '~/components/ProductPlay.vue';
import type { FreePickProduct } from '~/stores/types/cart';

const props = defineProps<{ product: FreePickProduct; badge?: string }>();
const image = computed(() => props.product.featuredImage?.node?.sourceUrl);
const specs = computed(() => props.product.productSpecs?.node);
const hasPreview = computed(() => !!specs.value?.audioDemos?.some(demo => demo.url) || !!specs.value?.productSoundcloudCode);
const playerDetails = computed(() => ({
  productId: props.product.productId,
  productName: props.product.name,
  productLink: props.product.uri,
  productImage: image.value,
  productVendor: props.product.productVendor,
  defaultCurrency: props.product.defaultCurrency,
  audioDemos: specs.value?.audioDemos ?? [],
  soundcloudId: specs.value?.productSoundcloudCode,
  soundcloudIdType: specs.value?.soundcloudShortcode?.includes('/playlists/') ? 'playlist' : 'track',
}));
</script>

<style scoped lang="scss">
.free-pick-product {
  display: flex;
  flex-direction: column;
  gap: .5rem;
  min-width: 0;
  text-align: left;

  &__cover {
    aspect-ratio: 1;
    background: #e6e6e8;
    display: grid;
    place-items: center;
    position: relative;
    overflow: hidden;
  }
  &__cover img { height: 100%; width: 100%; object-fit: cover; }
  &__placeholder { padding: .75rem; overflow-wrap: anywhere; }
  &__badge { position: absolute; top: .5rem; left: .5rem; padding: .15rem .4rem; background: #fff; font-size: .7rem; }
  &__play { position: absolute; background: rgb(255 255 255 / 92%); border-radius: 50%; color: #1c1c1f; }
  &__name { font-size: .9rem; font-weight: 600; margin: 0; overflow-wrap: anywhere; }
  &__vendor { font-size: .8rem; color: #66666c; margin: 0; overflow-wrap: anywhere; }
  &__price { display: flex; flex-wrap: wrap; gap: .4rem; font-size: .8rem; margin: 0; }
  &__price s { color: #66666c; }
  &__price strong { color: #36762b; }
  :deep(button:focus-visible), a:focus-visible { outline: 2px solid #1c1c1f; outline-offset: 3px; }
}
</style>
